import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

interface DateTimePickerProps {
  date?: Date | null;
  onDateChange: (date: Date | null) => void;
  disabled?: (date: Date) => boolean;
  placeholder?: string;
}

const DateTimePicker: React.FC<DateTimePickerProps> = ({ 
  date, 
  onDateChange, 
  disabled = () => false,
  placeholder = "Select appointment date and time"
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(date || null);
  const [selectedTime, setSelectedTime] = useState<string>(
    date ? date.toTimeString().slice(0, 5) : '12:00'
  );
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  // Update internal state when external date prop changes
  useEffect(() => {
    setSelectedDate(date || null);
    if (date) {
      setSelectedTime(date.toTimeString().slice(0, 5));
    }
  }, [date]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (dropdownRef.current && !dropdownRef.current.contains(target) && 
          buttonRef.current && !buttonRef.current.contains(target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Generate calendar days
  const generateCalendarDays = (year: number, month: number): (Date | null)[] => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days: (Date | null)[] = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const [currentMonth, setCurrentMonth] = useState<number>(
    selectedDate ? selectedDate.getMonth() : new Date().getMonth()
  );
  const [currentYear, setCurrentYear] = useState<number>(
    selectedDate ? selectedDate.getFullYear() : new Date().getFullYear()
  );

  const monthNames: string[] = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const weekDays: string[] = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const handleDateSelect = (day: Date | null): void => {
    if (!day || disabled(day)) return;
    
    const [hours, minutes] = selectedTime.split(':').map(Number);
    const newDateTime = new Date(day);
    newDateTime.setHours(hours, minutes, 0, 0);
    
    setSelectedDate(newDateTime);
    onDateChange(newDateTime);
    setIsOpen(false);
  };

  const handleTimeChange = (time: string): void => {
    setSelectedTime(time);
    
    if (selectedDate) {
      const [hours, minutes] = time.split(':').map(Number);
      const newDateTime = new Date(selectedDate);
      newDateTime.setHours(hours, minutes, 0, 0);
      
      setSelectedDate(newDateTime);
      onDateChange(newDateTime);
    }
  };

  const navigateMonth = (direction: 'prev' | 'next'): void => {
    if (direction === 'prev') {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    } else {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    }
  };

  const formatDisplayDate = (date: Date | null): string => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }) + ' at ' + date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const isToday = (date: Date | null): boolean => {
    if (!date) return false;
    const today = new Date();
    return date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
  };

const isSameDay = (date1: Date | null, date2: Date | null): boolean => {
  if (!date1 || !date2) return false;

  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  );
};

  const calendarDays = generateCalendarDays(currentYear, currentMonth);

  return (
    <div className="relative w-full">
      {/* Input Field */}
      <div
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white cursor-pointer hover:border-gray-400 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-all duration-200 flex items-center justify-between"
      >
        <div className="flex items-center flex-1">
          <Calendar className="w-5 h-5 text-gray-400 mr-3" />
          <span className={`${selectedDate ? 'text-gray-900' : 'text-gray-500'} text-sm`}>
            {selectedDate ? formatDisplayDate(selectedDate) : placeholder}
          </span>
        </div>
      </div>

      {/* Dropdown Calendar */}
      {isOpen && (
        <div 
          ref={dropdownRef}
          className="absolute z-50 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl p-4 w-80 left-0"
        >
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={() => navigateMonth('prev')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-4 h-4 text-gray-600" />
            </button>
            <h3 className="text-sm font-semibold text-gray-900">
              {monthNames[currentMonth]} {currentYear}
            </h3>
            <button
              type="button"
              onClick={() => navigateMonth('next')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1 mb-4">
            {/* Week days header */}
            {weekDays.map(day => (
              <div key={day} className="text-center text-xs font-medium text-gray-500 p-2">
                {day}
              </div>
            ))}
            
            {/* Calendar days */}
            {calendarDays.map((day, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleDateSelect(day)}
                disabled={!day || disabled(day)}
                className={`
                  p-2 text-sm rounded-lg transition-all duration-150 hover:bg-gray-100
                  ${!day ? 'invisible' : ''}
                  ${day && disabled(day) ? 'text-gray-300 cursor-not-allowed hover:bg-transparent' : 'text-gray-700'}
                  ${isToday(day) ? 'bg-blue-50 text-blue-600 font-semibold' : ''}
                  ${isSameDay(day, selectedDate) ? 'bg-blue-500 text-white hover:bg-blue-600' : ''}
                `}
              >
                {day ? day.getDate() : ''}
              </button>
            ))}
          </div>

          {/* Time Picker */}
          <div className="border-t border-gray-100 pt-4">
            <div className="mb-2">
              <span className="text-sm font-medium text-gray-700">Time</span>
            </div>
            <input
              type="time"
              value={selectedTime}
              onChange={(e) => handleTimeChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DateTimePicker;